import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import texte from './texte';

// Sprachumschaltung Deutsch/Englisch. Die Startsprache bestimmt bereits das Inline-Skript im
// <head> von public/index.html (?lang= > localStorage > Deutsch) und schreibt sie nach <html lang>,
// damit der Preloader bei Englisch auf die Uebersetzung des statischen HTML warten kann.

export const SPRACHEN = ['de', 'en'];
const SPEICHER_SCHLUESSEL = 'sprache';

const startSprache = () => {
  const lang = typeof document !== 'undefined' ? document.documentElement.lang : '';
  return SPRACHEN.includes(lang) ? lang : 'de';
};

const uebersetze = (sprache, schluessel) => texte[sprache][schluessel] ?? texte.de[schluessel] ?? schluessel;

// Statische Teile aus index.html (Navigation, Hero, Kontakt, Footer) tragen data-i18n
// (Textinhalt) bzw. data-i18n-attr="title:schluessel;aria-label:schluessel" (Attribute)
export function wendeStatischeTexteAn(sprache) {
  const wurzel = document.documentElement;
  wurzel.lang = sprache;
  document.title = uebersetze(sprache, 'seite.titel');
  document.querySelector('meta[name="description"]')?.setAttribute('content', uebersetze(sprache, 'seite.beschreibung'));

  document.querySelectorAll('[data-i18n]').forEach((el) => {
    el.textContent = uebersetze(sprache, el.dataset.i18n);
  });
  document.querySelectorAll('[data-i18n-attr]').forEach((el) => {
    el.dataset.i18nAttr.split(';').forEach((paar) => {
      const [attribut, schluessel] = paar.split(':');
      el.setAttribute(attribut.trim(), uebersetze(sprache, schluessel.trim()));
    });
  });

  // Preloader wartete bei Englisch auf diesen Schritt (s. index.html)
  if (wurzel.classList.contains('sprache-ausstehend')) {
    wurzel.classList.remove('sprache-ausstehend');
    window.preloaderPruefen?.();
  }
}

const SpracheKontext = createContext({ sprache: 'de', wechsleSprache: () => {}, t: (s) => uebersetze('de', s) });

export function SpracheProvider({ children }) {
  const [sprache, setSprache] = useState(startSprache);

  const wechsleSprache = useCallback((neu) => {
    setSprache(neu);
    try {
      localStorage.setItem(SPEICHER_SCHLUESSEL, neu);
    } catch (e) {
      // Privater Modus o. ae.: Wahl gilt dann nur fuer diesen Besuch
    }
    // ?lang= in der Adresse mitfuehren: Englisch bleibt so beim Teilen des Links erhalten,
    // und ein veraltetes ?lang=en ueberstimmt die neue Wahl beim Neuladen nicht
    const adresse = new URL(window.location.href);
    if (neu === 'de') adresse.searchParams.delete('lang');
    else adresse.searchParams.set('lang', neu);
    window.history.replaceState(null, '', adresse);
  }, []);

  useEffect(() => {
    wendeStatischeTexteAn(sprache);
  }, [sprache]);

  // Der Umschalter liegt in der statischen Navbar (index.html), ausserhalb des React-Baums
  useEffect(() => {
    const knopf = document.getElementById('sprach-umschalter');
    if (!knopf) return undefined;
    const klick = () => wechsleSprache(document.documentElement.lang === 'en' ? 'de' : 'en');
    knopf.addEventListener('click', klick);
    return () => knopf.removeEventListener('click', klick);
  }, [wechsleSprache]);

  const wert = useMemo(
    () => ({ sprache, wechsleSprache, t: (schluessel) => uebersetze(sprache, schluessel) }),
    [sprache, wechsleSprache]
  );

  return <SpracheKontext.Provider value={wert}>{children}</SpracheKontext.Provider>;
}

export const useSprache = () => useContext(SpracheKontext);
